import { Avatar, Center, Flex, Spinner, Button } from '@chakra-ui/react';
import { isDefined } from '@duke-hq/lib';
import { ChatMeta, getChatTypeText } from '@duke-hq/svc-support-web';
import { ChatItem } from '@duke-hq/ui/components/Chat/ChatItem';
import { t } from '@lingui/core/macro';
import { useContext } from 'react';
import { ListProps, ListItems } from '@duke-hq/ui/components/ListDetail/ListDetail';
import { djs } from '@duke-hq/djs';
import { SupportContext } from './ctx';

export function ChatListItems(props: ListProps<ChatMeta, string>) {
  const {
    items,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    actions: { previousPage, nextPage },
  } = props;

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      backgroundColor="bg.surface"
      height="100%"
      width="100%"
      overflow="scroll"
    >
      {isLoading ? (
        <Spinner ml="4" size="xs" />
      ) : (
        <ChatItems selectedId={props.selectedId} items={items} isLoading={!!isLoading} />
      )}

      <Flex justifyContent="space-between" width="100%">
        <Flex flexGrow={1} width="30%" justifyContent="stretch" alignSelf="stretch">
          {hasPreviousPage ? (
            <Button disabled={!hasPreviousPage} onClick={previousPage} variant="link" margin="4">
              {t`Previous`}
              {isFetchingPreviousPage ? <Spinner width="8px" height="8px" pl="2" /> : null}
            </Button>
          ) : null}
        </Flex>

        <Flex flexGrow={1} width="30%" justifyContent="end">
          {hasNextPage ? (
            <Button
              disabled={isFetchingNextPage}
              onClick={nextPage}
              variant="link"
              justifyContent="flex-end"
              alignSelf="stretch"
              margin="4"
            >
              {isFetchingNextPage ? <Spinner width="8px" height="8px" pr="2" /> : null}
              {t`Next`}
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
}

export function ChatItems({
  selectedId,
  items,
  isLoading,
}: {
  selectedId?: string;
  items: ListItems<ChatMeta, string>;
  isLoading: boolean;
}) {
  const { actions } = useContext(SupportContext);

  if (!isLoading) {
    if (!items || items.length === 0) {
      return <EmptyState />;
    }
  }

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width="100%"
      flexGrow={1}
      overflowY="scroll"
    >
      {items.map((item) => {
        const data = item.data;
        const names: string[] = [];

        let avatars;
        let updatedAt = '';

        const participantDrns = data ? [data.userDrn, ...data.recentUsers].filter(isDefined) : [];
        const users = participantDrns.map(actions.getUser).filter(isDefined);

        if (data?.recentUsers) {
          if (typeof data.modified === 'string') {
            updatedAt = djs(data.modified).fromNow();
          }

          names.push(...users.map((user) => user.displayName).filter(isDefined));
          avatars = users.map((user) => {
            return (
              <Avatar
                key={`${item.id}#${user.displayName}`}
                size="sm"
                name={user.displayName ?? undefined}
                src={user.avatar ?? undefined}
              />
            );
          });
        }

        const { chatType } = data || {};
        let chatTypeText = chatType != null ? getChatTypeText(chatType) : '';
        if (item?.data?.isArchived) {
          chatTypeText = `${chatType} [Archived]`;
        } else if (item?.data?.status === 0) {
          chatTypeText = `${chatType} [Closed]`;
        }

        return (
          <ChatItem
            key={item.id}
            title={names.join(', ')}
            avatars={avatars}
            chatType={chatTypeText}
            updatedAt={updatedAt}
            message={item?.data?.mostRecentMessage ?? ''}
            onClick={item.selectItem}
            bg={selectedId === item.id ? 'bg.muted' : 'bg.surface'}
            w="full"
          />
        );
      })}
    </Flex>
  );
}

function EmptyState() {
  return <Center padding="4">{t`No support chats.`}</Center>;
}
